/**
 * Økonomi for one årgang (PAGES.md — «Økonomi i kort form: forbruk mot budsjett», and the event's
 * own Economy page, sak 48).
 *
 * ---------------------------------------------------------------------------------------------
 * THE PAGE IS A SUMMARY, NOT A FORM, AND THE CONTRACT SAYS SO IN ITS SHAPE.
 *
 * «Kostnadslinjer legges uansett inn fra Performances og People, så siden er en samlevisning, ikke
 * et skjema.» So the primary answer is `EditionEconomy`: one row per category, budget against
 * spend, already subtracted. A client that had to compute the difference would be a client that
 * could compute it differently from the next one.
 *
 * The LINES are a separate, paged answer — there is no bound on how many a festival enters, which
 * is exactly why the summary is not «read them all and add them up in the browser».
 *
 * ---------------------------------------------------------------------------------------------
 * EVERY AMOUNT IS AN INTEGER OF MINOR UNITS, AND NEVER A STRING OR A FLOAT.
 *
 * 199,50 kr is `19950`. The formatting — thousands, the comma, the currency after the number in
 * Norwegian — belongs to the client and to `Intl.NumberFormat`, which knows the locale this reader
 * has. An API that sent «kr 199,50» would have decided that for every client including the mobile
 * app, and would have made the number unusable for arithmetic on the way.
 *
 * ---------------------------------------------------------------------------------------------
 * ONE CURRENCY PER YEAR ON THE SUMMARY, WHICH IS AN HONEST SIMPLIFICATION RATHER THAN A LIMIT.
 *
 * A line carries its own currency, because a foreign booking is invoiced in its own. The SUMMARY
 * reports the year's dominant currency and counts only lines in it, plus how many it left out —
 * `otherCurrencyLines`. The alternative is a rate table, which is a product decision (which rate,
 * from when, who updates it) rather than a line of code, and one nobody has asked for yet. A
 * number silently mixing kroner and euros would be worse than either.
 */
import { z } from 'zod';

import { costLineKindSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { costPerSchema } from './resources.js';

/** What a description may be. Long enough for «Lydleie inkl. rigg», short enough for a row. */
export const MAX_COST_DESCRIPTION = 200;

/** How many categories one organisation may hold. A chart of accounts, not a ledger. */
export const MAX_COST_CATEGORIES = 60;

/**
 * The ceiling on one line: ±10 000 000 000 minor units — a hundred million kroner.
 *
 * Not a business rule; a guard against a typo becoming a budget. `bigint` in the database holds far
 * more, and a festival that genuinely needs a larger line raises the constant rather than
 * discovering the limit at the bank.
 */
export const MAX_COST_AMOUNT_MINOR = 10_000_000_000;

export const costCategoryDtoSchema = z
  .object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    sortOrder: z.number().int(),
    /**
     * HONORARER FØRES HER (vedtak 85).
     *
     * Nøyaktig én kategori i huset kan bære merket, og et hus uten merke er en lovlig tilstand —
     * da skrives ingen honorarlinje, og kontraktfanen sier fra framfor at tallet lander i en
     * tilfeldig konto.
     */
    feeDefault: z.boolean(),
    /**
     * HVILKEN BUDSJETTLINJE OVERFØRINGEN FØRER HER (sak 120.2, migrering 0183).
     *
     * Atle, 13. september: «Kategorien får en rolle (honorar, reise, opphold, mat) satt én gang
     * i Økonomi, og overføringen slår opp. Samme form som fee_default.»
     *
     * Null er den vanlige tilstanden og en lovlig en: da får linja kategorien man velger i
     * overføringsdialogen, som er nøyaktig oppførselen huset hadde før. Et hus som ikke har delt
     * kontoplanen sin i fire skal ikke få linjene sine spredt på kategorier de ikke har bedt om.
     *
     * `fee` og `feeDefault` er det SAMME merket sett fra to sider — en skranke i basen holder dem
     * i takt — og feltet over blir stående fordi kontraktfanen og honorarlinja leser det.
     */
    budgetRole: z.enum(['fee', 'travel', 'accommodation', 'food']).nullable(),
  })
  .meta({ id: 'CostCategoryDto' });
export type CostCategoryDto = z.infer<typeof costCategoryDtoSchema>;

/** Not paged: a chart of accounts is read whole, and `MAX_COST_CATEGORIES` bounds it. */
export const costCategoryListResponseSchema = z
  .object({ items: z.array(costCategoryDtoSchema) })
  .meta({ id: 'CostCategoryListResponse' });
export type CostCategoryListResponse = z.infer<typeof costCategoryListResponseSchema>;

export const createCostCategoryRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    sortOrder: z.number().int().optional(),
    feeDefault: z.boolean().optional(),
    budgetRole: z.enum(['fee', 'travel', 'accommodation', 'food']).nullish(),
  })
  .meta({ id: 'CreateCostCategoryRequest' });
export type CreateCostCategoryRequest = z.infer<typeof createCostCategoryRequestSchema>;

/** The key never moves — it is what a clone and an import match a category across years by. */
export const updateCostCategoryRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    sortOrder: z.number().int().optional(),
    /**
     * Å sette merket her FLYTTER det: kategorien som hadde det, mister det i samme skriving.
     * Alternativet — en feilmelding om at en annen kategori er merket — ville tvunget den som
     * bytter kontoplan til å gjøre to operasjoner i riktig rekkefølge for å oppnå én ting.
     */
    feeDefault: z.boolean().optional(),
    /**
     * Å sette en rolle FLYTTER den, som merket over: kategorien som hadde «reise» mister den i
     * samme skriving. `null` fjerner rollen uten å gi den til noen.
     */
    budgetRole: z.enum(['fee', 'travel', 'accommodation', 'food']).nullish(),
  })
  .meta({ id: 'UpdateCostCategoryRequest' });
export type UpdateCostCategoryRequest = z.infer<typeof updateCostCategoryRequestSchema>;

export const costLineDtoSchema = z
  .object({
    id: z.uuid(),
    /**
     * LINJA HAR ETT ANKER, OG BARE ETT (sak 115, migrering 0149).
     *
     * Festivalens linjer henger på årgangen; byråets på sin egen henvendelse. Byrået har ingen
     * årgang å henge dem i, og §4.2 krever at de to husenes tall ligger fra hverandre framfor i
     * samme tabell med et flagg. Skranken i basen sier nøyaktig én av de to er satt.
     */
    editionId: z.uuid().nullable(),
    bookingEnquiryId: z.uuid().nullable(),
    categoryId: z.uuid(),
    /** Resolved for display, so a list of lines does not have to join the chart itself. */
    categoryName: z.string(),
    performanceId: z.uuid().nullable(),
    /** The act this arose on, when it arose on one. Null for a line that belongs to the year. */
    performanceTitle: z.string().nullable(),
    kind: costLineKindSchema,
    description: z.string(),
    /** Minor units, signed. See the header: never a float, never a formatted string. */
    amountMinor: z.number().int(),
    currency: z.string(),
    incurredOn: z.string().nullable(),
    /**
     * «Utgifter som skal faktureres tilbake til artist / management» (Atle, 2. september).
     *
     * On the LINE and not on a second list: it is a question about who ends up paying this one
     * expense, and the expense is already recorded where it arose.
     */
    rebill: z.boolean(),
    /**
     * Linja signeringen skrev (vedtak 85). Den kan rettes og fjernes som alle andre; merket sier
     * bare at tallet kom fra kontraktens honorar, slik at Økonomi kan si det og koden kan finne
     * den igjen neste gang honoraret endres.
     */
    artistFee: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'CostLineDto' });
export type CostLineDto = z.infer<typeof costLineDtoSchema>;

export const COST_LINE_SORT_KEYS = ['incurredOn', 'amount'] as const;
export type CostLineSortKey = (typeof COST_LINE_SORT_KEYS)[number];

export const listCostLinesQuerySchema = cursorQuerySchema(COST_LINE_SORT_KEYS)
  .extend({
    /** Festivalens side: årgangens linjer. Utelates når det spørres på en henvendelse. */
    editionId: z.uuid().optional(),
    /** Byråets side: linjene på DENNE henvendelsen (sak 115). */
    bookingEnquiryId: z.uuid().optional(),
    /** One booking's own lines — the Økonomi tab on a performance. */
    performanceId: z.uuid().optional(),
    categoryId: z.uuid().optional(),
    kind: costLineKindSchema.optional(),
  })
  .meta({ id: 'ListCostLinesQuery' });
export type ListCostLinesQuery = z.infer<typeof listCostLinesQuerySchema>;

export const costLineListResponseSchema = page(costLineDtoSchema, 'CostLineListResponse');
export type CostLineListResponse = z.infer<typeof costLineListResponseSchema>;

const amount = z.number().int().min(-MAX_COST_AMOUNT_MINOR).max(MAX_COST_AMOUNT_MINOR);

export const createCostLineRequestSchema = z
  .object({
    /** Nøyaktig én av disse to. Tjenesten avviser både null og begge. */
    editionId: z.uuid().nullish(),
    bookingEnquiryId: z.uuid().nullish(),
    categoryId: z.uuid(),
    performanceId: z.uuid().nullish(),
    kind: costLineKindSchema,
    description: z.string().trim().min(1).max(MAX_COST_DESCRIPTION),
    amountMinor: amount,
    currency: z
      .string()
      .trim()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    /** `YYYY-MM-DD`. Refused on a budget line by the database and by the service. */
    incurredOn: z.iso.date().nullish(),
    /** Absent is false — every line recorded before the column existed is the organiser's own. */
    rebill: z.boolean().optional(),
  })
  .meta({ id: 'CreateCostLineRequest' });
export type CreateCostLineRequest = z.infer<typeof createCostLineRequestSchema>;

/** `kind` og ankeret flytter seg aldri: en budsjettlinje som ble et forbruk er en ny linje. */
export const updateCostLineRequestSchema = z
  .object({
    categoryId: z.uuid().optional(),
    description: z.string().trim().min(1).max(MAX_COST_DESCRIPTION).optional(),
    amountMinor: amount.optional(),
    incurredOn: z.iso.date().nullish(),
    rebill: z.boolean().optional(),
  })
  .meta({ id: 'UpdateCostLineRequest' });
export type UpdateCostLineRequest = z.infer<typeof updateCostLineRequestSchema>;

/**
 * One row of «forbruk mot budsjett». `remainingMinor` is `budget - actual`, subtracted here so two
 * clients cannot disagree about whether an overspend is a negative remainder or a positive one.
 */
export const economyCategoryRowSchema = z
  .object({
    categoryId: z.uuid(),
    categoryName: z.string(),
    budgetMinor: z.number().int(),
    actualMinor: z.number().int(),
    remainingMinor: z.number().int(),
    /** How many spend lines stand behind the number, so «0 kr» and «ingen linjer» are distinct. */
    lineCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'EconomyCategoryRow' });
export type EconomyCategoryRow = z.infer<typeof economyCategoryRowSchema>;

/**
 * What one resource costs this year (201.17): «Kostnad på ressursen (leie per dag/per bruk) som går
 * til Økonomi.» DERIVED from the resource's price and its allocations, never stored — a cost line
 * would go stale the moment somebody moved a booking.
 *
 * `quantity` is 1 for `total`, the number of allocations for `use`, and the number of distinct
 * local dates in use for `day`, and the hours booked for `hour` (276.6, to decimals: a booking of
 * 90 minutes is 1.5). Cancelled and removed allocations count for nothing.
 */
export const economyResourceCostRowSchema = z
  .object({
    resourceId: z.uuid(),
    resourceName: z.string(),
    resourceTypeName: z.string(),
    costPer: costPerSchema,
    unitCostMinor: z.number().int().nonnegative(),
    quantity: z.number().nonnegative(),
    amountMinor: z.number().int().nonnegative(),
  })
  .meta({ id: 'EconomyResourceCostRow' });
export type EconomyResourceCostRow = z.infer<typeof economyResourceCostRowSchema>;

export const economyResourceCostsSchema = z
  .object({
    /** A resource's price carries no currency of its own, so it is read as the house's: NOK. */
    currency: z.string(),
    totalMinor: z.number().int().nonnegative(),
    /**
     * Whether `totalMinor` is inside the year's `actualMinor`. False only when the year's summary is
     * in another currency — adding kroner to euros is the one thing the header forbids.
     */
    inTotal: z.boolean(),
    items: z.array(economyResourceCostRowSchema),
  })
  .meta({ id: 'EconomyResourceCosts' });
export type EconomyResourceCosts = z.infer<typeof economyResourceCostsSchema>;

export const editionEconomySchema = z
  .object({
    editionId: z.uuid(),
    /** The year's dominant currency; every amount below is in it. See the header. */
    currency: z.string(),
    /**
     * Lines in some OTHER currency, which this summary did not count. Zero on almost every year;
     * when it is not zero the page says so rather than quietly reporting a smaller total.
     */
    otherCurrencyLines: z.number().int().nonnegative(),
    budgetMinor: z.number().int(),
    /** The categories' spend plus the resource costs when `resources.inTotal`. */
    actualMinor: z.number().int(),
    remainingMinor: z.number().int(),
    categories: z.array(economyCategoryRowSchema),
    resources: economyResourceCostsSchema,
  })
  .meta({ id: 'EditionEconomy' });
export type EditionEconomy = z.infer<typeof editionEconomySchema>;
